import {
  Alert,
  AlertIcon,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useOrderSideEffectJobs,
  useOrderSideEffectJobSummary,
} from '@/hooks/useOrderConsistencyData'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { OrderSideEffectJobsTable } from '@/components/orders/OrderSideEffectJobsTable'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import type { OrderSideEffectJobStatus, OrderSideEffectJobType } from '@/types'

const JOB_STATUSES: OrderSideEffectJobStatus[] = [
  'pending',
  'processing',
  'retry_scheduled',
  'failed',
  'completed',
  'cancelled',
]

const JOB_TYPES: OrderSideEffectJobType[] = [
  'cache_sync',
  'payment_realtime_notify',
  'order_notification_dispatch',
  'payment_confirmation_fast_path',
  'receipt_generation',
  'contract_initialization',
  'admin_monitoring_sync',
  'merchant_webhook_event',
]

type OrderSideEffectJobsMonitorProps = {
  isActive: boolean
}

const formatActiveAge = (seconds: number | null | undefined, fallback: string): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return fallback
  }

  const totalSeconds = Math.floor(seconds)
  const days = Math.floor(totalSeconds / 86_400)
  const hours = Math.floor((totalSeconds % 86_400) / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const remainingSeconds = totalSeconds % 60
  const formatUnit = (value: number) => value.toString().padStart(2, '0')

  return `${formatUnit(days)} D ${formatUnit(hours)} H ${formatUnit(minutes)} M ${formatUnit(
    remainingSeconds
  )} S`
}

export function OrderSideEffectJobsMonitor({ isActive }: OrderSideEffectJobsMonitorProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [jobType, setJobType] = useState('')
  const [orderId, setOrderId] = useState('')
  const limit = 20
  const summaryQuery = useOrderSideEffectJobSummary({ enabled: isActive })
  const jobsQuery = useOrderSideEffectJobs(
    {
      page,
      limit,
      orderId: orderId || undefined,
      status: status || undefined,
      jobType: jobType || undefined,
    },
    { enabled: isActive }
  )
  const summary = summaryQuery.data
  const pageData = jobsQuery.data
  const statusOptions = [
    {
      value: '',
      label: t('monitoring.sideEffectJobs.filters.defaultStatuses'),
    },
    ...JOB_STATUSES.map(value => ({
      value,
      label: t(`monitoring.sideEffectJobs.statuses.${value}`),
    })),
  ]
  const jobTypeOptions = [
    { value: '', label: t('common.all') },
    ...JOB_TYPES.map(value => ({
      value,
      label: t(`monitoring.sideEffectJobs.jobTypes.${value}`),
    })),
  ]

  const handleRefresh = async () => {
    await Promise.all([summaryQuery.refetch(), jobsQuery.refetch()])
  }

  const metrics = [
    { key: 'pending', value: summary?.counts.pending ?? 0 },
    { key: 'processing', value: summary?.counts.processing ?? 0 },
    { key: 'retryScheduled', value: summary?.counts.retryScheduled ?? 0 },
    { key: 'failed', value: summary?.counts.failed ?? 0 },
    { key: 'due', value: summary?.dueCount ?? 0 },
    {
      key: 'oldestActive',
      value: formatActiveAge(summary?.oldestActiveSeconds, t('common.na')),
      columnSpan: 2,
    },
  ]
  return (
    <VStack align="stretch" spacing={4}>
      {summaryQuery.isLoading && !summary && (
        <HStack justify="center" py={6}>
          <Spinner size="sm" />
          <Text fontSize="sm">{t('common.loading')}</Text>
        </HStack>
      )}

      {summaryQuery.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {summaryQuery.error instanceof Error
            ? summaryQuery.error.message
            : t('common.operationFailed')}
        </Alert>
      )}

      {summary && (
        <SimpleGrid columns={{ base: 2, md: 7 }} spacing={2}>
          {metrics.map(metric => (
            <Card
              key={metric.key}
              borderRadius="md"
              variant="outline"
              gridColumn={metric.columnSpan === 2 ? 'span 2' : undefined}
            >
              <CardBody py={2} px={3}>
                <HStack justify="space-between" spacing={2}>
                  <Text fontSize="xs" color="gray.500" isTruncated>
                    {t(`monitoring.sideEffectJobs.metrics.${metric.key}`)}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
                    {metric.value}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <HStack align="end" spacing={3} flexWrap="wrap">
        <FormControl maxW="260px">
          <FormLabel fontSize="xs">
            {t('monitoring.sideEffectJobs.filters.orderId')}
          </FormLabel>
          <Input
            size="sm"
            value={orderId}
            onChange={event => {
              setOrderId(event.target.value)
              setPage(1)
            }}
          />
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel fontSize="xs">
            {t('monitoring.sideEffectJobs.filters.status')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={status}
            options={statusOptions}
            onChange={value => {
              setStatus(value)
              setPage(1)
            }}
          />
        </FormControl>
        <FormControl maxW="260px">
          <FormLabel fontSize="xs">
            {t('monitoring.sideEffectJobs.filters.jobType')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={jobType}
            options={jobTypeOptions}
            onChange={value => {
              setJobType(value)
              setPage(1)
            }}
          />
        </FormControl>
        <RefreshIconButton
          label={t('common.refresh')}
          onClick={handleRefresh}
          isLoading={summaryQuery.isFetching || jobsQuery.isFetching}
        />
      </HStack>

      <OrderSideEffectJobsTable
        jobs={pageData?.jobs ?? []}
        isLoading={jobsQuery.isLoading}
        error={jobsQuery.error}
        observedAt={pageData?.observedAt ?? summary?.observedAt}
      />

      {pageData && pageData.pagination.totalPages > 1 && (
        <HStack justify="space-between">
          <Text fontSize="sm" color="gray.500">
            {t('common.pagination.pageOf', {
              current: pageData.pagination.page,
              total: pageData.pagination.totalPages,
              defaultValue: `Page ${pageData.pagination.page} of ${pageData.pagination.totalPages}`,
            })}
          </Text>
          <HStack>
            <Button
              size="sm"
              onClick={() => setPage(current => Math.max(1, current - 1))}
              isDisabled={page <= 1}
            >
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              onClick={() => setPage(current => current + 1)}
              isDisabled={page >= pageData.pagination.totalPages}
            >
              {t('common.next')}
            </Button>
          </HStack>
        </HStack>
      )}
    </VStack>
  )
}
